import React, {
  memo,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { Select, message, Button } from 'antd';
import { useTranslation } from 'react-i18next';
import { cache } from 'swr';
import KeycloakContext from '@providers/Keycloak/KeycloakContext';
import SessionContext from '@providers/Session/SessionContext';
import api from '@services/api';
import { HiOutlineSwitchHorizontal } from 'react-icons/hi';
import { treatCustomerName } from '@utils/changeDocumentTitle';
import { useNavigate } from 'react-router-dom';
import SelectContextItem from './SelectContextItem';
import { ICustomerContextOption } from './selectContext.types';

interface SelectContextProps {
  width: string;
  className?: string;
}

const SelectContext = ({ width, className }: SelectContextProps) => {
  const { t } = useTranslation('main');
  const navigate = useNavigate();
  const { keycloak } = useContext(KeycloakContext);
  const {
    setMainCustomerInContext,
    isContextSwitchingEnabled,
    customersSwitch,
    setIsContextSwitch,
    isMotBusiness,
  } = useContext(SessionContext);
  const [selectedCustomerOption, setSelectedCustomerOption] = useState<
    number | undefined
  >();
  const [showSwitchOptions, setShowSwitchOptions] = useState(false);

  const putCustomersSwitch = useCallback(
    (customerId: string | number) =>
      api.put(`/users/customers-switch/${customerId}`),
    [],
  );

  const customerSelected = useMemo(() => {
    const allOptions =
      customersSwitch && Object.values(customersSwitch).slice(0, 2)?.flat();
    return allOptions?.find(item => item.id === selectedCustomerOption);
  }, [customersSwitch, selectedCustomerOption]);

  useEffect(() => {
    if (customersSwitch) {
      const customerParent = customersSwitch.options?.[0];
      setMainCustomerInContext(customerParent);
      setSelectedCustomerOption(customersSwitch.selected);
    }
  }, [customersSwitch, setMainCustomerInContext]);

  const mapOptions = useCallback(
    (options: any) =>
      options?.map(({ id, ...item }: ICustomerContextOption) => ({
        value: id,
        label: <SelectContextItem id={id} {...item} />,
        filterProps: `${item.name};${item.tradingName};${item.cnpj};${id}`,
      })),
    [],
  );

  const selectOptions = useMemo(() => {
    const isOptionsEmpty = customersSwitch?.options?.length === 0;
    const isCustomerOptionsEmpty =
      customersSwitch?.customersOptions?.length === 0;
    const options = !isOptionsEmpty
      ? mapOptions(customersSwitch?.options) ?? []
      : [];

    if (!isCustomerOptionsEmpty) {
      options.push({
        label: t(
          `context-switch.${isMotBusiness ? 'my-departments' : 'my-customers'}`,
        ),
        options: mapOptions(customersSwitch?.customersOptions) ?? [],
      });
    }
    return options;
  }, [
    customersSwitch?.options,
    customersSwitch?.customersOptions,
    mapOptions,
    t,
    isMotBusiness,
  ]);

  // Change token
  const onCustomerChange = useCallback(
    async (value: any) => {
      setIsContextSwitch(true);

      try {
        await putCustomersSwitch(value);
        await keycloak.updateToken(Number.MAX_VALUE);
        window.location.search = '';
        navigate('/');
        window.location.reload();
        cache.clear();
      } catch {
        message.error(t('context-switch.msgs.error'));
        setIsContextSwitch(false);
      }
    },
    [keycloak, navigate, putCustomersSwitch, setIsContextSwitch, t],
  );

  const selectionDisabled = useMemo(
    () => !customersSwitch || !selectedCustomerOption,
    [customersSwitch, selectedCustomerOption],
  );

  return (
    <>
      {showSwitchOptions && isContextSwitchingEnabled ? (
        <Select
          className={className}
          popupClassName="select-context-switch"
          style={{
            marginRight: '5px',
            width: selectionDisabled ? '34px' : width,
          }}
          onBlur={() => setShowSwitchOptions(false)}
          value={selectedCustomerOption}
          onChange={onCustomerChange}
          options={selectOptions}
          disabled={selectionDisabled}
          loading={selectionDisabled}
          showSearch
          autoFocus
          defaultOpen
          filterOption={(input, option) => {
            const verifyString = (value: any) =>
              String(value).toLowerCase().indexOf(input.toLowerCase()) >= 0;
            // @ts-ignore
            return verifyString(option?.filterProps);
          }}
        />
      ) : (
        <Button
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            cursor:
              isContextSwitchingEnabled && selectOptions?.length > 0
                ? 'pointer'
                : 'default',
          }}
          onMouseOver={() => setShowSwitchOptions(true)}
          loading={!customerSelected}
          onClick={() => setShowSwitchOptions(true)}
        >
          <span
            style={{
              fontWeight: 'bold',
              textTransform: 'uppercase',
              fontSize: '0.85rem',
              color: 'var(--primary-color)',
            }}
          >
            {treatCustomerName(
              customerSelected?.nickname || customerSelected?.name,
            )}
          </span>
          {isContextSwitchingEnabled && (
            <HiOutlineSwitchHorizontal
              style={{
                marginLeft: 8,
                fontSize: '1rem',
              }}
            />
          )}
        </Button>
      )}
    </>
  );
};

export default memo(SelectContext);
